import { computed, readonly, ref, shallowRef, type ComputedRef, type Ref } from "vue";

import { classifyFailure, type FailureKind } from "@/composables/useNotify";

/** Where a store's load stands. 'idle' is "never asked" (or reset since), which a view renders like 'loading'. */
export type LoadStatus = 'idle' | 'loading' | 'success' | 'error';

export interface LoadState {
    readonly status: Readonly< Ref< LoadStatus > >;
    /**
     * The rejection of the last load that failed, kept through a retry until that retry settles, so a view can hold
     * its error box (with a dimmed "Try again") instead of flashing back to a skeleton. AsyncState's `error` prop.
     */
    readonly error: ComputedRef< unknown >;
    /** What kind of failure `error` is ('offline' | 'timeout' | 'server' | 'other'), or null while there is none. */
    readonly failure: ComputedRef< FailureKind | null >;
    start(): void;
    succeed(): void;
    fail( err: unknown ): void;
    /** Called from the store's reset(): a new session has not asked for anything yet. */
    reset(): void;
}

/**
 * data-12 / errors-4: goFetch resolved null for a timeout or a dropped connection, so a store could not tell "failed"
 * from "empty" and its view said "No results" or "No positions" with no way to retry. A store that loads through the
 * throwing path (`forceThrowExceptions` / goFetchOrThrow) records the outcome here, and its view feeds `status`/`error`
 * to AsyncState.
 *
 * Plain refs, no store access: declare it at module scope beside the store's other state (module-scope stores).
 */
export function createLoadStatus(): LoadState {
    const status = ref< LoadStatus >( 'idle' );
    const error = shallowRef< unknown >( undefined );
    // Lazy: classified only when a view reads it, not on every failure. Cached per error, and navigator.onLine is not
    // reactive, so an 'offline' reading stays until the next start()/fail() even if the connection returns meanwhile.
    const failure = computed< FailureKind | null >( () => undefined === error.value ? null : classifyFailure( error.value ) );

    return {
        status: readonly( status ),
        // A computed, not readonly(): that would hand out a deep-readonly proxy of the error rather than the error.
        error: computed< unknown >( () => error.value ),
        failure,
        start(): void {
            status.value = 'loading';
        },
        succeed(): void {
            status.value = 'success';
            error.value = undefined;
        },
        fail( err: unknown ): void {
            // `undefined` would read as "no error" to AsyncState and toAsyncStatus.
            error.value = err ?? new Error( 'The load failed.' );
            status.value = 'error';
        },
        reset(): void {
            status.value = 'idle';
            error.value = undefined;
        }
    };
}
